import { useEffect, useId, useRef, useState } from 'react';
import { IconAngleDown, IconAngleUp } from '@icons';
import { NestedOption, NestedOptionType, OptionGroup } from '../../types';
import { toggleNestedRegionOption } from '../../utils/regionDistrictOptions';
import './RegionDistrictNestedSelect.css';

export type RegionDistrictNestedSelectProps = {
    optionGroups: OptionGroup[];
    value: NestedOption[];
    onChange: (next: NestedOption[]) => void;
    placeholder?: string;
    disabled?: boolean;
    ariaLabel?: string;
};

export const RegionDistrictNestedSelect = ({
    optionGroups,
    value,
    onChange,
    placeholder = 'Select regions and districts',
    disabled = false,
    ariaLabel = 'Regions and districts',
}: RegionDistrictNestedSelectProps) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const generatedId = useId();
    const listboxId = `${generatedId}-listbox`;
    const triggerId = `${generatedId}-trigger`;

    useEffect(() => {
        if (!open) return;

        const handlePointerDown = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    const triggerLabel = (() => {
        if (value.length === 0) return placeholder;
        if (value.some((o) => o.type === 'all')) {
            return 'All Regions, Districts, and Locations';
        }
        return `${value.length} selected`;
    })();

    const handleToggle = (option: NestedOption) => {
        onChange(toggleNestedRegionOption(option, value, optionGroups));
    };

    return (
        <div className="rdn-root" ref={rootRef}>
            <button
                type="button"
                id={triggerId}
                className="rdn-trigger"
                disabled={disabled}
                aria-label={ariaLabel}
                aria-expanded={open}
                aria-controls={listboxId}
                onClick={() => setOpen((prev) => !prev)}
            >
                <span>{triggerLabel}</span>
                <span className="rdn-chevron" aria-hidden="true">
                    {open ? (
                        <IconAngleUp iconClasses="rdn-chevron-icon" />
                    ) : (
                        <IconAngleDown iconClasses="rdn-chevron-icon" />
                    )}
                </span>
            </button>

            {open && (
                <div className="rdn-panel" id={listboxId} role="group" aria-labelledby={triggerId}>
                    {optionGroups.map((group) => (
                        <div key={group.label} className="rdn-group">
                            {group.options.map((opt) => {
                                const indent =
                                    opt.type === 'district' ? 1 : opt.type === 'location' ? 2 : 0;
                                const typePrefix =
                                    opt.type === 'all'
                                        ? 'Global Access: '
                                        : opt.type === 'region'
                                          ? 'Region: '
                                          : opt.type === 'district'
                                            ? 'District: '
                                            : opt.type === 'location'
                                              ? 'Location: '
                                              : '';
                                const checked =
                                    value.some((o) => o.type === 'all') ||
                                    value.some((o) => o.value === opt.value);

                                return (
                                    <label
                                        key={opt.value}
                                        className={`rdn-option rdn-indent-${indent}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() =>
                                                handleToggle({
                                                    label: opt.label,
                                                    value: opt.value,
                                                    type: opt.type as NestedOptionType,
                                                })
                                            }
                                        />
                                        <span className="rdn-option-text">
                                            <span className="rdn-type-prefix">{typePrefix}</span>
                                            {opt.label}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}

            {value.length > 0 && (
                <div className="rdn-tags">
                    {value.map((opt) => (
                        <span key={opt.value} className="rdn-tag">
                            {opt.label.trim()}
                            <button
                                type="button"
                                disabled={disabled}
                                onClick={() => onChange(value.filter((o) => o.value !== opt.value))}
                                aria-label={`Remove ${opt.label.trim()}`}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};
